"use client";

import { useCallback, useEffect, useState } from "react";
import { Button, Typography } from "@cappelendamm/design-system/react";
import { useAuth } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { syncSchoolApprovalForCurrentUser } from "@/organizations/school/activeSchool";
import {
  COOLDOWN_MS,
  cooldownStorageKey,
  getRemainingCooldownMs,
} from "@/organizations/school/refreshCooldown";

const readLastRunAt = (key: string): number | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const formatRemaining = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * Lets a signed-in teacher re-check their school's approval status without a full
 * re-login. Approval is a manual process in Crystallize, and the result is cached
 * in the session token, so this reruns the same refresh the org switcher does and
 * mints a fresh token. A naive per-school localStorage cooldown throttles spamming.
 */
export const RefreshSchoolApprovalButton = () => {
  const { sessionClaims, getToken } = useAuth();
  const router = useRouter();

  const orgNumber =
    sessionClaims?.orgSlug ?? sessionClaims?.publicMeta?.activeSchool ?? null;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    if (!orgNumber) return;

    const key = cooldownStorageKey(orgNumber);
    const tick = () =>
      setRemainingMs(getRemainingCooldownMs(readLastRunAt(key), Date.now()));

    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [orgNumber]);

  const handleRefresh = useCallback(async () => {
    if (!orgNumber || isRefreshing || remainingMs > 0) return;

    setIsRefreshing(true);
    try {
      await syncSchoolApprovalForCurrentUser(orgNumber);
      await getToken({ skipCache: true });

      const now = Date.now();
      window.localStorage.setItem(cooldownStorageKey(orgNumber), String(now));
      setRemainingMs(COOLDOWN_MS);

      router.refresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [orgNumber, isRefreshing, remainingMs, getToken, router]);

  if (!orgNumber) return null;

  const isCoolingDown = remainingMs > 0;
  const disabled = isRefreshing || isCoolingDown;

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="secondary-small"
        onClick={handleRefresh}
        disabled={disabled}
      >
        {isRefreshing ? "Oppdaterer..." : "Oppdater skolekundestatus"}
      </Button>
      {isCoolingDown && (
        <Typography variant="body-xs">
          Du kan prøve igjen om {formatRemaining(remainingMs)}.
        </Typography>
      )}
    </div>
  );
};
